// @flow

import styled, { type StyledComponent } from "styled-components";
import Skeleton from "react-loading-skeleton";

import Button from "app/components/shared/Button";
import Emojify from "app/components/shared/Emojify";
import emptyStateGradient from "app/images/analytics/emptyStateGradient.svg";

type Props = {
  heading: string,
  subheading?: string,
  emoji?: string,
  fullscreen?: boolean,
  gradient?: boolean,
  link?: string,
  loadingRows?: boolean,
  loadingPanels?: boolean,
};

const Wrapper: StyledComponent<
  { fullscreen?: boolean, gradient?: boolean },
  empty,
  HTMLDivElement,
> = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  background: ${({ gradient }) =>
    gradient ? `url(${emptyStateGradient});` : "var(--slate-100)"};
  background-size: cover;
  border-radius: 8px;
  padding: 50px;
  height: ${({ fullscreen }) => (fullscreen ? "100%" : "auto")};
`;

const StateWrapper: StyledComponent<{}, empty, HTMLDivElement> = styled.div`
  text-align: center;
  max-width: 400px;

  h2 {
    margin: 20px 0 0;
    color: var(--charcoal-700);
    font-weight: 500;
  }

  p {
    margin: 10px 0 0;
    color: var(--charcoal-700);
    font-weight: 400;
    white-space: pre-wrap;
  }

  a.btn {
    margin-top: 32px;
  }
`;

const StyledSkeleton = styled.div`
  margin-top: 40px;
  width: 870px;
  max-width: 100%;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 60%;
    background: linear-gradient(transparent, white);
  }

  .panels {
    display: grid;
    grid-template-columns: auto auto auto;
    gap: 10px;
    margin-bottom: 10px;

    @media (max-width: 800px) {
      grid-template-columns: auto;
    }

    .panel {
      flex-direction: column;
      justify-content: flex-end;
      gap: 10px;
      height: 120px;

      &:nth-child(odd) {
        > span:nth-child(1) {
          width: 60%;
        }

        > span:nth-child(2) {
          width: 100%;
        }
      }

      &:nth-child(even) {
        > span:nth-child(1) {
          width: 100%;
        }

        > span:nth-child(2) {
          width: 30%;
        }
      }
    }
  }

  .row,
  .panel {
    display: flex;
    padding: 20px 30px;
    background-color: var(--base-0);
    filter: drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.09))
      drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.04))
      drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.02))
      drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.01));
    border-radius: 4px;

    > span {
      height: 10px;

      .react-loading-skeleton {
        display: block;
        opacity: 0.4;
        border-radius: 5px;
        height: 100%;
        background-color: var(--slate-500);
      }
    }
  }

  .row {
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 10px;

    > span {
      &:nth-child(1) {
        width: 50%;
      }

      &:nth-child(2) {
        width: 20%;
      }
    }
  }
`;

const EmptyState = ({
  heading,
  subheading,
  emoji,
  fullscreen,
  gradient,
  link,
  loadingRows,
  loadingPanels,
}: Props) => (
  <Wrapper fullscreen={fullscreen} gradient={gradient}>
    <StateWrapper>
      {emoji && (
        <Emojify text={emoji} style={{ fontSize: 45 }} title={heading} />
      )}

      <h2>{heading}</h2>

      {subheading && <p>{subheading}</p>}

      {link && (
        <Button href={link} theme="primary">
          Learn more in Docs
        </Button>
      )}
    </StateWrapper>

    {(loadingRows || loadingPanels) && (
      <StyledSkeleton>
        {loadingPanels && (
          <div className="panels">
            {[...Array(3)].map((_el, index) => (
              <div key={index} className="panel">
                <Skeleton containerTestId="loading-panel" />
                <Skeleton containerTestId="loading-panel" />
              </div>
            ))}
          </div>
        )}

        {loadingRows &&
          [...Array(3)].map((_el, index) => (
            <div key={index} className="row">
              <Skeleton containerTestId="loading-row" />
              <Skeleton containerTestId="loading-row" />
            </div>
          ))}
      </StyledSkeleton>
    )}
  </Wrapper>
);

export default EmptyState;
